import { useState, useCallback, useEffect } from 'react'

import { useNavigate } from 'react-router-dom'

import { authContextDefaultValue } from 'auth/context/useAuthContext'
import {
  useRefreshTokenMutation,
  useVerifyTokenMutation,
} from 'store/auth/authApi'
import { authActions } from 'store/auth/authSlice'
import { AuthResponseType } from 'store/auth/types'
import { useAppDispatch } from 'store/hooks'

import { AuthErrorEnum, AuthState } from '../types'
import { getAuthReasonStatus, removeToken, storeToken } from '../utils'

/**
 * Hook to manage authentication state and actions
 */
export const useAuthState = (initialToken: string | null) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [verifyToken, verifyTokenResult] = useVerifyTokenMutation()
  const [refreshToken, refreshTokenResult] = useRefreshTokenMutation()

  const [auth, setAuth] = useState<AuthState>({
    ...authContextDefaultValue,
    jwt: initialToken,
  })

  // Update AuthState with new token data
  const updateAuthState = useCallback(
    (tokenData: AuthResponseType) => {
      if (!tokenData) {
        return false
      }

      const { token, charlieUser, user } = tokenData

      // Update localStorage
      storeToken(token)

      // Update state
      setAuth({
        charlieUser,
        user,
        jwt: token,
        isLoading: false,
        isAuthenticated: true,
      })

      // Still update Redux state as majority of the app relies on it
      dispatch(authActions.setJWT(token))

      return true
    },
    [dispatch]
  )

  // Logout function - centralized to handle all cleanup
  const logout = useCallback(
    (reason: AuthErrorEnum) => {
      removeToken()

      setAuth({
        charlieUser: null,
        user: null,
        jwt: null,
        isLoading: false,
        isAuthenticated: false,
      })

      navigate(`/auth?status=${getAuthReasonStatus(reason)}`, { replace: true })
    },
    [navigate]
  )

  // Handle verification error
  useEffect(() => {
    if (verifyTokenResult.isError) {
      if (
        'status' in verifyTokenResult.error &&
        verifyTokenResult.error.status === 403
      ) {
        logout(AuthErrorEnum.SETUP)
      } else {
        logout(AuthErrorEnum.INVALID)
      }
    }
  }, [
    verifyTokenResult.isError,
    refreshTokenResult.isError,
    verifyTokenResult.error,
    logout,
  ])

  // Handle  refresh errors
  useEffect(() => {
    if (refreshTokenResult.isError) {
      logout(AuthErrorEnum.INVALID)
    }
  }, [verifyTokenResult.isError, refreshTokenResult.isError, logout])

  // Verify token (for initial auth check)
  const verifyCurrentToken = useCallback(async (): Promise<boolean> => {
    setAuth(prev => ({ ...prev, isLoading: true }))
    const result = await verifyToken().unwrap()
    return updateAuthState(result)
  }, [verifyToken, updateAuthState])

  // Refresh token (to be called when token is aging)
  const refreshCurrentToken = useCallback(async (): Promise<boolean> => {
    setAuth(prev => ({ ...prev, isLoading: true }))
    const result = await refreshToken().unwrap()
    return updateAuthState(result)
  }, [refreshToken, updateAuthState])

  return {
    auth,
    logout,
    verifyCurrentToken,
    refreshCurrentToken,
  }
}
